import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Spin,
  Descriptions,
  message,
  Typography,
  Row,
  Col,
  Tabs,
  Table,
  Empty,
  Tag,
  Tooltip,
  Modal,
  Form,
  Input,
  InputNumber,
} from "antd";
import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  EditOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { API_BASE_URL } from "../api/api";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const { Text } = Typography;
const { TabPane } = Tabs;
const { confirm } = Modal;

const UserDetailPage = () => {
  const navigate = useNavigate();
  const { memberId } = useParams();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [vacationOrders, setVacationOrders] = useState([]);
  const [vacationLoading, setVacationLoading] = useState(false);
  const [hotelOrders, setHotelOrders] = useState([]);
  const [hotelLoading, setHotelLoading] = useState(false);
  const [pointsTab, setPointsTab] = useState("earned");
  const [pointsData, setPointsData] = useState([]);
  const [pointsLoading, setPointsLoading] = useState(false);

  const [adjustModalVisible, setAdjustModalVisible] = useState(false);
  const [form] = Form.useForm();

  const token = localStorage.getItem("token");
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));

  const fetchUserDetail = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}user&member_id=${memberId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await res.json();
      const userData = result.data?.[0];
      if (!userData) {
        message.warning("No user data found.");
        return;
      }
      setUser(userData);
      fetchVacationOrders();
      fetchPointsData("earned");
      // console.log("userinfo", userInfo);
    } catch (err) {
      console.error(err);
      message.error("Failed to load user details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserDetail();
  }, [memberId]);

  const getMembershipLevel = (level) => {
    if (level === 1) {
      return (
        <span
          style={{
            color: "black",
            backgroundColor: "#f0f0f0",
            padding: "4px 8px",
            borderRadius: "8px",
            fontWeight: 500,
            display: "inline-block",
          }}
        >
          Basic
        </span>
      );
    } else if (level === 2) {
      return (
        <span
          style={{
            color: "white",
            backgroundColor: "orange",
            padding: "4px 8px",
            borderRadius: "8px",
            fontWeight: 500,
            display: "inline-block",
          }}
        >
          Gold
        </span>
      );
    } else {
      return (
        <span
          style={{
            color: "white",
            backgroundColor: "gray",
            padding: "4px 8px",
            borderRadius: "8px",
            fontWeight: 500,
            display: "inline-block",
          }}
        >
          Unknown
        </span>
      );
    }
  };

  const formatValue = (value) =>
    value ? value : <Text type="secondary">N/A</Text>;

  const fetchVacationOrders = async () => {
    setVacationLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}vacationPackageOrder&member_id=${memberId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const result = await res.json();
      setVacationOrders(result.data || []);
    } catch (err) {
      console.error(err);
      message.error("Failed to load vacation package orders.");
    } finally {
      setVacationLoading(false);
    }
  };
  const fetchHotelOrders = async () => {
    setHotelLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}hotelOrder&member_id=${memberId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const result = await res.json();
      setHotelOrders(result.data || []);
    } catch (err) {
      console.error(err);
      message.error("Failed to load hotel orders.");
    } finally {
      setHotelLoading(false);
    }
  };

  const fetchPointsData = async (type) => {
    setPointsLoading(true);
    let endpoint = "";
    switch (type) {
      case "earned":
        endpoint = `pointsEarned&member_id=${memberId}`;
        break;
      case "redeemed":
        endpoint = `pointsRedeem&member_id=${memberId}`;
        break;
      case "cashout":
        endpoint = `pointsCashOut&member_id=${memberId}`;
        break;
      case "action_log":
        endpoint = `pointsActionLog&member_id=${memberId}`;
        break;
      default:
        return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await res.json();
      setPointsData(result.data || []);
    } catch (err) {
      console.error(err);
      message.error("Failed to load points data.");
    } finally {
      setPointsLoading(false);
    }
  };

  const vacationColumns = [
    {
      title: "Order ID",
      dataIndex: "order_id",
      key: "order_id",
      render: (order_id) => (
        <Link to={`/vacation-orders/${order_id}`}>{order_id}</Link>
      ),
    },
    {
      title: "Tour Operator",
      dataIndex: "tour_operator",
      key: "tour_operator",
    },
    { title: "Hotel", dataIndex: "hotel_name", key: "hotel_name" },
    {
      title: "Destination",
      dataIndex: "destination_name",
      key: "destination_name",
    },
    { title: "Departure", dataIndex: "departure_date", key: "departure_date" },
    { title: "Return", dataIndex: "return_date", key: "return_date" },
    {
      title: "Total Cost",
      dataIndex: "total_trip_cost",
      key: "total_trip_cost",
    },
    {
      title: "Passenger Count",
      dataIndex: "passenger_count",
      key: "passenger_count",
    },
    {
      title: "Booked At",
      dataIndex: "add_time",
      key: "add_time",
      render: (value) =>
        value ? dayjs.utc(value).local().format("YYYY-MM-DD HH:mm") : "-",
    },
  ];

  const hotelColumns = [
    {
      title: "Hotel Name",
      dataIndex: ["data", "data", "hotel", "name"],
      key: "hotel_name",
    },
    {
      title: "Booking ID",
      dataIndex: ["data", "data", "bookingId"],
      key: "booking_id",
      render: (booking_id) => (
        <Link to={`/hotel-orders/${booking_id}`}>{booking_id}</Link>
      ),
    },
    {
      title: "Status",
      dataIndex: ["data", "data", "status"],
      key: "status",
      render: (status) => (
        <Tag color={status === "CONFIRMED" ? "green" : "red"}>{status}</Tag>
      ),
    },
    {
      title: "Check-In",
      dataIndex: ["data", "data", "checkin"],
      key: "checkin",
    },
    {
      title: "Check-Out",
      dataIndex: ["data", "data", "checkout"],
      key: "checkout",
    },
    {
      title: "Booked Guest",
      dataIndex: ["data", "data", "holder"],
      key: "holder",
      render: (holder) =>
        holder ? `${holder.firstName} ${holder.lastName}` : "N/A",
    },
    {
      title: "Price (CAD)",
      dataIndex: ["data", "data", "price"],
      key: "price",
      render: (value) => `$${Number(value).toFixed(2)}`,
    },
    {
      title: "Booked At",
      dataIndex: ["data", "data", "createdAt"],
      key: "createdAt",
      render: (v) =>
        v ? dayjs.utc(v).local().format("YYYY-MM-DD HH:mm") : "-",
    },
  ];

  const earnedColumns = [
    {
      title: "Order ID",
      dataIndex: "order_id",
      key: "order_id",
      render: (order_id) => (
        <Link to={`/vacation-orders/${order_id}`}>{order_id}</Link>
      ),
    },
    { title: "Points", dataIndex: "points", key: "points" },
    { title: "Valid Date", dataIndex: "valid_date", key: "valid_date" },
    {
      title: "Credited?",
      dataIndex: "credited",
      key: "credited",
      render: (credited) => {
        if (credited === 1) {
          return (
            <span style={{ color: "#52c41a", fontWeight: "bold" }}>Yes</span>
          );
        } else if (credited === 0) {
          return (
            <span style={{ color: "#ff4d4f", fontWeight: "bold" }}>
              Pending
            </span>
          );
        } else {
          return null; // Handle any other value if needed
        }
      },
    },
    {
      title: "Created At",
      dataIndex: "create_at",
      key: "create_at",
      render: (value) =>
        value ? dayjs.utc(value).local().format("YYYY-MM-DD HH:mm") : "-",
    },
    {
      title: "Updated At",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (value) =>
        value ? dayjs.utc(value).local().format("YYYY-MM-DD HH:mm") : "-",
    },
  ];

  const redeemedColumns = [
    {
      title: "Order Number",
      dataIndex: "order_number",
      key: "order_number",
      render: (order_number) => (
        <Link to={`/vacation-orders/${order_number}`}>{order_number}</Link>
      ),
    },
    {
      title: "Used Points",
      dataIndex: "points_used_for_redeem",
      key: "points_used_for_redeem",
    },
    { title: "Paid Amount", dataIndex: "paid_amount", key: "paid_amount" },
    {
      title: "Redeemed Amount",
      dataIndex: "redeemed_amount",
      key: "redeemed_amount",
    },
    { title: "Total Amount", dataIndex: "total_amount", key: "total_amount" },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (value) =>
        value ? dayjs.utc(value).local().format("YYYY-MM-DD HH:mm") : "-",
    },
    {
      title: "Updated At",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (value) =>
        value ? dayjs.utc(value).local().format("YYYY-MM-DD HH:mm") : "-",
    },
  ];

  const cashOutColumns = [
    {
      title: "Requested Points",
      dataIndex: "points_requested_for_redeem",
      key: "points_requested_for_redeem",
    },
    { title: "Cash", dataIndex: "cash", key: "cash" },
    {
      title: "Issued",
      dataIndex: "issued",
      key: "issued",
      render: (issued) => {
        if (issued === 1) {
          return <CheckCircleTwoTone twoToneColor="#52c41a" />;
        } else if (issued === 0) {
          return <CloseCircleTwoTone twoToneColor="#ff4d4f" />;
        } else {
          return null; // Handle any other value if needed
        }
      },
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (value) =>
        value ? dayjs.utc(value).local().format("YYYY-MM-DD HH:mm") : "-",
    },
    {
      title: "Updated At",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (value) =>
        value ? dayjs.utc(value).local().format("YYYY-MM-DD HH:mm") : "-",
    },
  ];
  const ActionLogColumns = [
    {
      title: "id",
      dataIndex: "id",
      key: "id",
    },
    { title: "Amount", dataIndex: "amount", key: "amount" },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
    },
    { title: "Operator", dataIndex: "operator", key: "operator" },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (value) =>
        value ? dayjs.utc(value).local().format("YYYY-MM-DD HH:mm") : "-",
    },
  ];

  const getPointsColumns = () => {
    switch (pointsTab) {
      case "earned":
        return earnedColumns;
      case "redeemed":
        return redeemedColumns;
      case "cashout":
        return cashOutColumns;
      case "action_log":
        return ActionLogColumns;
      default:
        return [];
    }
  };

  const showConfirmDelete = () => {
    confirm({
      title: "Are you sure you want to deactivate this account?",
      content:
        "This action can only be reversed later by contacting IT support.",
      okText: "Yes, Deactivate",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const res = await fetch(
            `${API_BASE_URL}deleteUser&member_id=${memberId}`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ member_id: memberId }),
            }
          );
          const result = await res.json();
          if (result.success) {
            message.success("Account status updated");
            setTimeout(() => {
              navigate("/users"); // Adjust the route as needed
            }, 1000);
          } else {
            message.error("Failed to update status");
          }
        } catch (err) {
          console.error(err);
          message.error("Error updating status");
        }
      },
    });
  };

  const handleToggleAutoRenew = () => {
    if (user.auto_renew === 0) {
      // Prompt user before enabling auto-renew
      Modal.confirm({
        title: "Enable Auto Renew?",
        icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
        content:
          "This will immediately reactivate user's subscription with Stripe.",
        okText: "Yes, Enable",
        cancelText: "Cancel",
        onOk: async () => {
          try {
            const res = await fetch(
              "https://secure.sunbook.ca/membership/reactivate_request",
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  membership_id: user.member_id, // Ensure this is the correct ID
                  email: user.email,
                }),
              }
            );

            const result = await res.json();

            if (result.success) {
              message.success(result.success);
              setUser({ ...user, auto_renew: 1 });
            } else {
              message.error(
                result.error || "Failed to reactivate subscription"
              );
            }
          } catch (error) {
            console.error("Reactivation error:", error);
            message.error("An error occurred while reactivating");
          }
        },
      });
    }
  };

  const handleAdjustPointsSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        member_id: user.member_id,
        amount: values.amount,
        reason: values.reason,
        ...(userInfo.role_id !== 1 && {
          admin_password: values.adminPassword,
        }),
      };

      const res = await fetch(`${API_BASE_URL}adjustPoints`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const text = await res.text();
      const result = text ? JSON.parse(text) : {};

      if (result.success) {
        message.success("Points updated");
        setAdjustModalVisible(false);
        form.resetFields();
        // optionally refresh user points here
        fetchUserDetail();
      } else {
        message.error(result.message || "Failed to update points");
      }
    } catch (err) {
      console.error(err);
      message.error("Validation or server error");
    }
  };

  if (loading) return <Spin />;
  if (!user) return <div>No user found</div>;

  return (
    <div>
      <Button
        type="link"
        onClick={() => navigate(-1)}
        style={{
          marginBottom: 20,
          backgroundColor: "#f0f5ff", // Lighter blue or gray for back button
          color: "#1890ff", // Use blue text to maintain the link style
          border: "1px solid #1890ff", // Subtle border for contrast
          padding: "6px 16px",
          borderRadius: "4px",
          cursor: "pointer",
          fontWeight: "bold", // Makes the text more noticeable
        }}
      >
        ← Back
      </Button>

      <h2 style={{ marginBottom: 16 }}>
        User Details (Member ID: {user.member_id})
      </h2>

      <Row gutter={32}>
        <Col xs={24} md={12}>
          <Descriptions title="Basic Info" bordered size="middle" column={1}>
            <Descriptions.Item label="Email">
              {formatValue(user.email)}
            </Descriptions.Item>
            <Descriptions.Item label="First Name">
              {formatValue(user.user_first_name)}
            </Descriptions.Item>
            <Descriptions.Item label="Last Name">
              {formatValue(user.user_last_name)}
            </Descriptions.Item>
            <Descriptions.Item label="Middle Name">
              {formatValue(user.user_middle_name)}
            </Descriptions.Item>
            <Descriptions.Item label="Mobile">
              {formatValue(user.mobile).slice(0, 3) +
                "-" +
                formatValue(user.mobile).slice(3, 6) +
                "-" +
                formatValue(user.mobile).slice(6, 10)}
            </Descriptions.Item>
            <Descriptions.Item label="Date of Birth">
              {formatValue(user.user_dob)}
            </Descriptions.Item>
            <Descriptions.Item label="Last Login">
              {formatValue(
                dayjs
                  .utc(user.last_login_time)
                  .local()
                  .format("YYYY-MM-DD HH:mm")
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Account Created">
              {formatValue(
                dayjs.utc(user.add_time).local().format("YYYY-MM-DD HH:mm")
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Account Status">
              <div style={{ display: "flex", alignItems: "center" }}>
                {user.member_is_del === 1 ? (
                  <Tag color="gray" style={{ marginBottom: 0 }}>
                    Deleted
                  </Tag>
                ) : (
                  <>
                    <Tag
                      color={user.status === 1 ? "green" : "red"}
                      style={{ marginBottom: 0 }}
                    >
                      {user.status === 1 ? "Active" : "Inactive"}
                    </Tag>

                    <Tooltip title="Deactivate this account">
                      {user.status === 1 && user.membership_level_id === 1 && (
                        <Button
                          size="small"
                          type="primary"
                          icon={<CheckCircleOutlined />}
                          onClick={showConfirmDelete}
                          style={{ marginLeft: 8 }}
                        >
                          Disable
                        </Button>
                      )}
                    </Tooltip>
                  </>
                )}
              </div>
            </Descriptions.Item>
          </Descriptions>
        </Col>

        <Col xs={24} md={12}>
          <Descriptions
            title="Membership Info"
            bordered
            size="middle"
            column={1}
          >
            <Descriptions.Item label="Membership Plan">
              {getMembershipLevel(user.membership_level_id)}
            </Descriptions.Item>
            <Descriptions.Item label="Membership Start Time">
              {formatValue(
                dayjs
                  .utc(user.membership_start_date)
                  .local()
                  .format("YYYY-MM-DD HH:mm")
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Membership End Time">
              {formatValue(
                dayjs
                  .utc(user.membership_end_date)
                  .local()
                  .format("YYYY-MM-DD HH:mm")
              )}
            </Descriptions.Item>

            <Descriptions.Item label="Auto Renew">
              <div style={{ display: "flex", alignItems: "center" }}>
                {user.auto_renew === 1 ? (
                  <CheckCircleTwoTone
                    twoToneColor="#52c41a"
                    style={{ fontSize: 18 }}
                  />
                ) : user.auto_renew === 0 ? (
                  <CloseCircleTwoTone
                    twoToneColor="#ff4d4f"
                    style={{ fontSize: 18 }}
                  />
                ) : (
                  formatValue(null)
                )}

                {user.status === 1 &&
                  user.member_is_del === 0 &&
                  user.auto_renew === 0 && (
                    <Tooltip title={"Enable auto-renew"}>
                      <Button
                        size="small"
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        onClick={handleToggleAutoRenew}
                        style={{ marginLeft: 8 }}
                      >
                        {"Enable"}
                      </Button>
                    </Tooltip>
                  )}
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Member Points">
              <div style={{ display: "flex", alignItems: "center" }}>
                <span
                  style={{ fontWeight: 500, fontSize: 16, marginRight: 12 }}
                >
                  {user.member_points}
                </span>
                <Tooltip title="Add or deduct points">
                  {user.status === 1 &&
                    user.member_is_del === 0 &&
                    user.membership_level_id !== 1 && (
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => setAdjustModalVisible(true)}
                        type="primary"
                      >
                        Adjust
                      </Button>
                    )}
                </Tooltip>
              </div>
            </Descriptions.Item>
          </Descriptions>
        </Col>
      </Row>

      <h3 style={{ marginTop: 40 }}>Order History</h3>
      <Tabs
        defaultActiveKey="vacation"
        onChange={(key) => {
          if (key === "vacation") fetchVacationOrders();
          if (key === "hotel") fetchHotelOrders();
        }}
      >
        <TabPane tab="Vacation Package" key="vacation">
          {vacationLoading ? (
            <Spin />
          ) : vacationOrders.length === 0 ? (
            <Empty description="No vacation package orders" />
          ) : (
            <Table
              columns={vacationColumns}
              dataSource={vacationOrders}
              rowKey="id"
              scroll={{ x: true }}
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ["10", "25", "50", "100"],
              }}
            />
          )}
        </TabPane>
        <TabPane tab="Hotel" key="hotel">
          {hotelLoading ? (
            <Spin />
          ) : hotelOrders.length === 0 ? (
            <Empty description="No hotel orders" />
          ) : (
            <Table
              columns={hotelColumns}
              dataSource={hotelOrders}
              rowKey={(record) => record.booking_id}
              scroll={{ x: "max-content" }}
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ["10", "25", "50", "100"],
              }}
            />
          )}
        </TabPane>
      </Tabs>

      <h3 style={{ marginTop: 40 }}>Points History</h3>
      <Tabs
        defaultActiveKey="earned"
        onChange={(key) => {
          setPointsTab(key);
          fetchPointsData(key);
        }}
      >
        <TabPane tab="Earned" key="earned">
          {pointsLoading ? (
            <Spin />
          ) : pointsData.length === 0 ? (
            <Empty description="No earned points records" />
          ) : (
            <Table
              columns={getPointsColumns()}
              dataSource={pointsData}
              rowKey="id"
              scroll={{ x: true }}
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ["10", "25", "50", "100"],
              }}
            />
          )}
        </TabPane>
        <TabPane tab="Redeemed" key="redeemed">
          {pointsLoading ? (
            <Spin />
          ) : pointsData.length === 0 ? (
            <Empty description="No redeemed points records" />
          ) : (
            <Table
              columns={getPointsColumns()}
              dataSource={pointsData}
              rowKey="id"
              scroll={{ x: true }}
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ["10", "25", "50", "100"],
              }}
            />
          )}
        </TabPane>
        <TabPane tab="Cash Out" key="cashout">
          {pointsLoading ? (
            <Spin />
          ) : pointsData.length === 0 ? (
            <Empty description="No cash out records" />
          ) : (
            <Table
              columns={getPointsColumns()}
              dataSource={pointsData}
              rowKey="id"
              scroll={{ x: true }}
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ["10", "25", "50", "100"],
              }}
            />
          )}
        </TabPane>
        <TabPane tab="Action Log" key="action_log">
          {pointsLoading ? (
            <Spin />
          ) : pointsData.length === 0 ? (
            <Empty description="No cash out records" />
          ) : (
            <Table
              columns={getPointsColumns()}
              dataSource={pointsData}
              rowKey="id"
              scroll={{ x: true }}
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ["10", "25", "50", "100"],
              }}
            />
          )}
        </TabPane>
      </Tabs>

      <Modal
        title="Adjust Member Points"
        visible={adjustModalVisible}
        onCancel={() => setAdjustModalVisible(false)}
        onOk={handleAdjustPointsSubmit}
        okText={"Submit"}
      >
        <Form form={form} layout="vertical" autoComplete="off">
          <Form.Item
            label="Points (positive to add, negative to deduct)"
            name="amount"
            rules={[{ required: true, message: "Please enter valid integer" }]}
          >
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            label="Reason"
            name="reason"
            rules={[{ required: true, message: "Please specify the reason" }]}
          >
            <Input.TextArea rows={3} autoComplete="off" />
          </Form.Item>

          {userInfo.role_id !== 1 && (
            <Form.Item
              label="Admin Password"
              name="adminPassword"
              rules={[
                { required: true, message: "Admin password is required" },
              ]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default UserDetailPage;
